import 'disposablestack/auto';
import { mixin } from './lib/mixin.js';
import {Fn} from "./lib/signal.js";
import {CallableDisposableStack, StackDisposable} from "./lib/disposable.js";
import {hookable, HookCallback, HookedFunction} from "./lib/hook.js";

export function registerClass(dest) {
    return function(source, ctx: ClassDecoratorContext) {
        dest.connect(() => ([source]));
    }
}

@mixin
export class PluginHelper {
    static Mixin : Function

    /**
     * listen to the signal identified by `signalName` on `object`. call `handler` every time the signal is emitted.
     * if `callWithArgs` is specified, call handler once at registration time with the arguments specified in therein.
     *
     * alternatively, if object is an EventTarget (e.g. DOM element), add an event listener to the DOM event identified
     * by `signalName`.
     *
     * the registration is automatically removed when the object, on which `slot` is called, gets disposed.
     *
     * @param object
     * @param signalName
     * @param handler a function to register as handler for the signal or event
     * @param callWithArgs
     */
    slot(object: EventTarget, signalName : string, handler : EventListener, callWithArgs?: any[]): EventListener;
    slot<F extends (...args: any) => any>(object: object, signalName : string | Function, handler : F, callWithArgs?: Parameters<F>): F;

    slot(object: EventTarget | object, signalName : string | Function, handler : Function, callWithArgs?: any[]): Function {
        if (object instanceof EventTarget && typeof signalName === 'string') {
            object.addEventListener(signalName, handler as EventListener);
            this[Symbol.dispose].defer(() => object.removeEventListener(signalName, handler as EventListener));
        } else if (signalName instanceof Function) {
            this[Symbol.dispose].use(signalName(object).connect(handler));
        } else if (typeof signalName === 'string') {
            this[Symbol.dispose].use(object[signalName].connect(handler));
        } else {
            throw 'invalid arguments';
        }
        if (callWithArgs) (handler as Function).apply(object, callWithArgs);
        return handler;
    }

    /**
     * hooks a method on an object
     * when the hooked method M(arg1, ...): ret is called, all registered handlers get called with h(next, arg1, ...),
     * and are expected to call next(arg1, ...) if the original method should be executed. they need to return the
     * return value returned by `next` themselves, or return any other value they want the hooked method to return.
     *
     * the hook is automatically removed when the object, on which `hook` is called, gets disposed.
     *
     * @param object
     * @param method
     * @param handler
     * @param callWithArgs
     */
    hook<MN extends string|number|symbol, P extends any[], R>(
        object: object & Record<MN, Fn<P, R>>,
        method: MN,
        handler: HookCallback<P, R>,
        callWithArgs?: any[]) {
        if (!(object[method] instanceof Function)) {
            throw `PluginHelper.hook called with object[method] not resolving to a Function (method: ${String(method)})`;
        }
        const descriptor = Object.getOwnPropertyDescriptor(object, method);
        let o : Fn<P, R> | HookedFunction<P, R> = descriptor?.get?.call?.(object) || descriptor?.value;
        if (!o || !('connect' in o)) {
            o = hookable(object[method], object);
            object[method] = o as (object & Record<MN, Fn<P, R>>)[MN];
        }
        var r = this[Symbol.dispose].use((o as HookedFunction<P, R>).connect(handler));
        if (callWithArgs) handler.apply(object, callWithArgs);
        return r;
    }
}


@PluginHelper.Mixin @StackDisposable.Mixin
export class PluginBase implements PluginHelper, StackDisposable {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    slot(object: EventTarget, signalName : string, handler : EventListener, callWithArgs?: any[]): EventListener;
    slot<F extends (...args: any) => any>(object: object, signalName : string | Function, handler : F, callWithArgs?: Parameters<F>): F;
    slot(object: EventTarget | object, signalName : string | Function, handler : Function, callWithArgs?: any[]): Function {
        throw new Error('Method not implemented.');
    }
    hook<MN extends string|number|symbol, P extends any[], R>(
        object: object & Record<MN, Fn<P, R>>,
        method: MN,
        handler: HookCallback<P, R>,
        callWithArgs?: any[]) {
        throw new Error('Method not implemented.');
    }
}
